import type { DevicePosition } from '@maru44/huntre-utils/src/models/device'
import type { DeviceMessage } from '@maru44/huntre-utils/src/models/deviceMessage'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { baseURL, serviceName } from 'src/config'
import { type CreateKumapPointRequest, useKumap } from 'src/hooks/kumap/useKumap'

type Props = {
  open: boolean
  onClose: () => void
  clientId: string
  deviceId: string
  msg: DeviceMessage
  deviceFallbackPosition: DevicePosition | null
  onPosted?: () => void
}

const CONTENT_PREFIX = `[捕獲] ${serviceName}(${baseURL})連携で報告\n\n`

const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: 36.3931, lng: 138.4444 }

type PointKind = CreateKumapPointRequest['point_kind_name']

const pointKindLabels: Record<PointKind, string> = {
  witness: '目撃',
  trace: '痕跡',
  injury: '人身被害',
  damage: '物損被害',
}

const witnessStateOptions: { value: 'adult' | 'cub' | 'with_cubs'; label: string }[] = [
  { value: 'adult', label: '成獣' },
  { value: 'cub', label: '子' },
  { value: 'with_cubs', label: '子連れ' },
]

const witnessGenderOptions: { value: 'male' | 'female'; label: string }[] = [
  { value: 'male', label: 'オス' },
  { value: 'female', label: 'メス' },
]

const traceKindOptions: {
  value: NonNullable<Extract<CreateKumapPointRequest, { point_kind_name: 'trace' }>['additional_data']>['trace_kind']
  label: string
}[] = [
  { value: 'footprint', label: '足跡' },
  { value: 'claw_mark', label: '爪痕' },
  { value: 'bark_stripping', label: 'クマ剥ぎ' },
  { value: 'feces', label: '糞' },
  { value: 'feeding_site', label: 'クマ棚' },
  { value: 'bear_nest', label: '土饅頭' },
  { value: 'feeding_remains', label: '食べた痕' },
  { value: 'other', label: 'その他' },
]

export const KumapPostDialog = ({ open, onClose, clientId, deviceId, msg, deviceFallbackPosition, onPosted }: Props) => {
  const { createKumapPoint, isCalling } = useKumap()

  const [pointKind, setPointKind] = useState<PointKind>('witness')
  const [content, setContent] = useState('')
  const [position, setPosition] = useState<google.maps.LatLngLiteral>(() => {
    if (msg.position) return { lat: msg.position.lat, lng: msg.position.lon }
    if (deviceFallbackPosition) return { lat: deviceFallbackPosition.lat, lng: deviceFallbackPosition.lon }
    return DEFAULT_CENTER
  })

  // msg にも device にも位置がない場合のみ、ブラウザの現在地取得を試みる
  useEffect(() => {
    if (msg.position || deviceFallbackPosition) return
    if (!navigator.geolocation) return
    navigator.geolocation.getCurrentPosition(
      (pos) => setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {
        // 拒否 or 取得失敗はハードコードのままにする
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // witness
  const [witnessCount, setWitnessCount] = useState<string>('')
  const [witnessState, setWitnessState] = useState<'' | 'adult' | 'cub' | 'with_cubs'>('')
  const [witnessGender, setWitnessGender] = useState<'' | 'male' | 'female'>('')

  // trace
  const [traceKind, setTraceKind] = useState<
    '' | 'footprint' | 'claw_mark' | 'bark_stripping' | 'feces' | 'feeding_site' | 'bear_nest' | 'feeding_remains' | 'other'
  >('')

  // injury
  const [injuryInjured, setInjuryInjured] = useState<string>('')
  const [injuryDied, setInjuryDied] = useState<string>('')

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
  })

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() })
    }
  }, [])

  const buildRequest = (): CreateKumapPointRequest | { error: string } => {
    const base = {
      content: `${CONTENT_PREFIX}${content}`,
      event_time: msg.createdAt.toDate().toISOString(),
      position,
    }
    switch (pointKind) {
      case 'witness': {
        const additional_data: Extract<CreateKumapPointRequest, { point_kind_name: 'witness' }>['additional_data'] = {}
        if (witnessCount) {
          const n = parseInt(witnessCount, 10)
          if (Number.isNaN(n) || n < 0) return { error: '頭数は 0 以上の整数で入力してください' }
          additional_data.count = n
        }
        if (witnessState) additional_data.state = witnessState
        if (witnessGender) additional_data.gender = witnessGender
        return { ...base, point_kind_name: 'witness', additional_data }
      }
      case 'trace': {
        if (!traceKind) return { error: '痕跡種別を選択してください' }
        return { ...base, point_kind_name: 'trace', additional_data: { trace_kind: traceKind } }
      }
      case 'injury': {
        const additional_data: Extract<CreateKumapPointRequest, { point_kind_name: 'injury' }>['additional_data'] = {}
        if (injuryInjured) {
          const n = parseInt(injuryInjured, 10)
          if (Number.isNaN(n) || n < 0) return { error: '負傷者数は 0 以上の整数で入力してください' }
          additional_data.count_injured = n
        }
        if (injuryDied) {
          const n = parseInt(injuryDied, 10)
          if (Number.isNaN(n) || n < 0) return { error: '死者数は 0 以上の整数で入力してください' }
          additional_data.count_died = n
        }
        return { ...base, point_kind_name: 'injury', additional_data }
      }
      case 'damage':
        return { ...base, point_kind_name: 'damage' }
    }
  }

  const handleSubmit = useCallback(async () => {
    const request = buildRequest()
    if ('error' in request) {
      toast.error(request.error)
      return
    }
    try {
      await createKumapPoint({
        clientId,
        deviceId,
        messageId: msg.id,
        content: `${CONTENT_PREFIX}${content}`,
        position,
        request,
      })
      toast.success('くまっぷに投稿しました')
      onPosted?.()
      onClose()
    } catch (e) {
      console.error(e)
      toast.error('くまっぷへの投稿に失敗しました')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, deviceId, msg.id, content, pointKind, position, witnessCount, witnessState, witnessGender, traceKind, injuryInjured, injuryDied])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>クマの捕獲を報告</DialogTitle>
      <DialogContent>
        <Stack
          spacing={3}
          sx={{
            mt: 1,
          }}
        >
          <Alert severity="info">
            <Typography variant="body2">
              <Link href={import.meta.env.VITE_KUMAP_BASE_URL} target="_blank" rel="noopener noreferrer" underline="hover">
                くまっぷ
              </Link>
              はクマの目撃・痕跡・被害情報を地図上で共有するサービスです。この報告は連携中のくまっぷにポイントとして投稿されます。
            </Typography>
          </Alert>
          <FormControl fullWidth>
            <InputLabel>ポイント種別</InputLabel>
            <Select id="pointKind" name="pointKind" label="ポイント種別" value={pointKind} onChange={(e) => setPointKind(e.target.value as PointKind)}>
              {(Object.keys(pointKindLabels) as PointKind[]).map((k) => (
                <MenuItem key={k} value={k}>
                  {pointKindLabels[k]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="内容"
            id="content"
            name="content"
            multiline
            minRows={3}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            helperText={`送信時に "${CONTENT_PREFIX}" が先頭に付きます`}
            fullWidth
          />

          {pointKind === 'witness' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">目撃情報</Typography>
              <TextField
                label="頭数"
                type="number"
                id="witnessCount"
                name="witnessCount"
                value={witnessCount}
                onChange={(e) => setWitnessCount(e.target.value)}
                slotProps={{
                  htmlInput: { min: 0 },
                }}
              />
              <FormControl fullWidth>
                <InputLabel>状態</InputLabel>
                <Select
                  id="witnessState"
                  name="witnessState"
                  label="状態"
                  value={witnessState}
                  onChange={(e) => setWitnessState(e.target.value as typeof witnessState)}
                >
                  <MenuItem value="">未選択</MenuItem>
                  {witnessStateOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>雌雄</InputLabel>
                <Select
                  id="witnessGender"
                  name="witnessGender"
                  label="雌雄"
                  value={witnessGender}
                  onChange={(e) => setWitnessGender(e.target.value as typeof witnessGender)}
                >
                  <MenuItem value="">未選択</MenuItem>
                  {witnessGenderOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          {pointKind === 'trace' && (
            <FormControl fullWidth required>
              <InputLabel>痕跡種別</InputLabel>
              <Select id="traceKind" name="traceKind" label="痕跡種別" value={traceKind} onChange={(e) => setTraceKind(e.target.value as typeof traceKind)}>
                {traceKindOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {pointKind === 'injury' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">人身被害情報</Typography>
              <TextField
                label="負傷者人数"
                type="number"
                id="injuryInjured"
                name="injuryInjured"
                value={injuryInjured}
                onChange={(e) => setInjuryInjured(e.target.value)}
                slotProps={{
                  htmlInput: { min: 0 },
                }}
              />
              <TextField
                label="死者人数"
                type="number"
                id="injuryDied"
                name="injuryDied"
                value={injuryDied}
                onChange={(e) => setInjuryDied(e.target.value)}
                slotProps={{
                  htmlInput: { min: 0 },
                }}
              />
            </Stack>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              位置情報 (地図をクリックで変更可)
            </Typography>
            {isLoaded ? (
              <GoogleMap mapContainerStyle={{ width: '100%', height: '360px' }} center={position} zoom={14} onClick={handleMapClick}>
                <MarkerF
                  position={position}
                  draggable
                  onDragEnd={(e) => {
                    if (e.latLng) setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() })
                  }}
                />
              </GoogleMap>
            ) : (
              <Typography>地図を読み込み中...</Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 1,
              }}
            >
              lat: {position.lat.toFixed(6)}, lng: {position.lng.toFixed(6)}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            p: 1,
            justifyContent: 'space-between',
          }}
        >
          <Button variant="outlined" onClick={onClose} disabled={isCalling}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isCalling}>
            投稿する
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
